import { HttpErrorResponse } from "@angular/common/http";
import { Component, Inject, OnInit, TemplateRef } from "@angular/core";
import { FormControl, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { WorkspaceUser } from "@app/_classes/timeghost";
import { ElRefDirective } from "@app/_directives/el-ref/el-ref.directive";
import { useAbsencesForUser } from "@app/_helpers/absence/utils";
import { ObserveFormGroupErrors, useFormErrorObservable } from "@app/_helpers/get-error-observable";
import { pushError } from "@app/_helpers/globalErrorHandler";
import { ErrorResponse, isWorkingHoursError, parseErrorResponse } from "@app/_helpers/is-error-object";
import parseSubscriptionAsStatus from "@app/_helpers/parseSubscriptionAsStatus";
import { hasPermissionByKey, hasUserRoleManager, isAdmin, isSubordinate, isSupervisor } from "@app/_helpers/permission";
import { createRxValueWithCache } from "@app/_helpers/rx-value";
import {
	getComegoFutureLockedDate,
	getComegoLockedDate,
	isComegoLockedForDate,
	isComegoLockedForDateFuture,
} from "@app/_helpers/timeLock";
import {
	coerceTimeFormat,
	createRxValue,
	DEFAULT_PERMISSION_GROUPS,
	flattenEntityResponse,
	fromRxValue,
	getActiveSchedule,
	hasPermission,
	hoursToFormat,
	nextTick,
	resolveRawArgs,
	stringify,
} from "@app/_helpers/utils";
import { CustomValidators } from "@app/_validators/custom-validators";
import { AppService } from "@app/app.service";
import { RecordToolbarService } from "@app/shared/record-toolbar/record-toolbar.service";
import { TimeDatePickerComponent } from "@app/shared/time-date-picker/time-date-picker.component";
import { TimeDatePickerConfig } from "@app/shared/time-date-picker/time-date-picker-config";
import { Order } from "@datorama/akita";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
	ComegoQuery,
	ComegoService,
	ComegoTime,
	ComegoTimeType,
	Logger,
	MyTimesService,
	UserSettings,
	UserSettingsQuery,
	WorkingHourSettings,
	Workspace,
} from "@timeghost/web-api";
import { addDays, format, isSameDay, isValid, roundToNearestMinutes, startOfDay } from "date-fns/esm";
import produce from "immer";
import { clamp } from "lodash-es";
import {
	combineLatest,
	combineLatestWith,
	distinctUntilChanged,
	filter,
	map,
	startWith,
	takeWhile,
	tap,
	timer,
} from "rxjs";
import { ComeGoTimeControl } from "../come-and-go-create-dialog/come-and-go-create-dialog.component";
import { COMEGO_ICON_MAP, COMEGO_MAX_DESCRIPTION } from "../come-and-go-create-dialog/come-and-go-utils";
import {
	UserSinglePickerDialogComponent,
	UserSinglePickerDialogData,
} from "../user-single-picker-dialog/user-single-picker-dialog.component";
export interface ComeGoUpdateDialogData {
	entity: ComegoTime;
	error?: ErrorResponse;
	fetchFromServer?: boolean;
	handleRuleErrors?: boolean;
	isPastTime?: boolean;
	recording?: boolean;
	enableSubmit?: boolean;
}
type UpdateComeGoTimeControl = ComeGoTimeControl;
const log = new Logger("ComeAndGoUpdateDialogComponent");
export const VerifyBlockComegoPast: (
	currentuser: () => UserSettings,
	choosenUser: () => string,
	start: () => string,
	end: () => string
) => ValidatorFn = (_currentuser, _choosenUser, _start, _end) => {
	return (form: FormControl<Date>) => {
		const currentuser = _currentuser();
		const choosenUser = _choosenUser();
		const blockComego = currentuser.workspace.settings.blockComego;
		if (!blockComego) return null;
		let date = form.getRawValue();
		if (isAdmin(currentuser)) {
			return null;
		}
		if (currentuser.id != choosenUser) {
			return;
		}

		const minDate = getComegoLockedDate(currentuser, choosenUser);
		const maxDate = getComegoFutureLockedDate(currentuser, choosenUser);

		if (blockComego.pastMode == "timePeriod" && blockComego.range == "hours") {
			const timeString = _start();
			const [hours, minutes] = timeString.split(":").map(Number);
			const startDate = date;
			startDate.setHours(hours, minutes, 0, 0);
			if (isComegoLockedForDate(currentuser, startDate)) {
				return { blockEdit: true };
			}
		}
		if (blockComego.futureMode == "timePeriod" && blockComego.futureRange == "hours") {
			const timeString = _end();
			const [hours, minutes] = timeString.split(":").map(Number);
			const endDate = date;
			endDate.setHours(hours, minutes, 0, 0);
			if (isComegoLockedForDateFuture(currentuser, endDate)) {
				return { blockEditFuture: true };
			}
		}
		if (minDate && maxDate) {
			if (date < minDate || date > maxDate) return { blockEdit: true };
			else return null;
		} else if (minDate) {
			if (date < minDate) return { blockEdit: true };
		} else if (maxDate) {
			if (date > maxDate) return { blockEdit: true };
		} else return null;
	};
};
export function generateTimeTableFromSchedule(user: UserSettings, now: Date = new Date()) {
	const schedule = getActiveSchedule(user, now);
	if (!schedule?.enabled || !schedule.items?.length) return null;
	const day = schedule.items?.find((x) => x.enabled && x.day === startOfDay(now).getUTCDay());
	if (!day) return null;
	const times =
		day.items?.reduce((acc, r, i, arr) => {
			const isLast = i === arr.length - 1;
			const nextItem = arr[i + 1];
			const addPauseItem = nextItem && r.to !== nextItem.from;
			acc.push({
				name: "comego.work",
				start: r.from,
				end: r.to,
				type: "work",
			} as any);
			if (addPauseItem)
				acc.push({
					name: "comego.pause",
					start: r.to,
					end: nextItem.from,
					type: "pause",
				} as any);
			return acc;
		}, [] as ComeGoTime[]) || [];
	return times;
}
export type ComeGoTime = Omit<ComegoTime, "workspace" | "id"> & { recording?: boolean; typeId?: string };
export type absenceSubcategories = {
	id: string;
	enabled: boolean;
	title: string;
	description?: string;
	durationType?: "basedOnTargetHours" | "customValue";
	duration?: string | null;
	isStatic?: boolean;
	visibleUsers: any[];
	visibleForGroups: any[];
};
@UntilDestroy()
@ObserveFormGroupErrors()
@Component({
	selector: "tg-come-and-go-update-dialog",
	templateUrl: "./come-and-go-update-dialog.component.html",
	styleUrls: ["./come-and-go-update-dialog.component.scss"],
})
export class ComeAndGoUpdateDialogComponent implements OnInit {
	readonly iconMap = COMEGO_ICON_MAP;
	readonly error$ = createRxValue<ErrorResponse>();
	readonly error$disabled = combineLatest([this.error$.asObservable(), this.userSettingsQuery.select()]).pipe(
		map(([err, user]) => {
			if (!err) return err;
			if (user.workspace?.schedules?.workspace?.workinghours?.preventDeviatingWorkingHours) return err;
			return {
				...err,
				type: "info",
			};
		})
	);
	absenceSubcategories: {
		id: string;
		enabled: boolean;
		title: string;
		description?: string;
		durationType?: "basedOnTargetHours" | "customValue";
		duration?: string | null;
		isStatic?: boolean;
		visibleUsers: any[];
		visibleForGroups: any[];
	}[] = [];
	constructor(
		private ref: MatDialogRef<ComeAndGoUpdateDialogComponent>,
		private dialog: MatDialog,
		private userSettingsQuery: UserSettingsQuery,
		private myTimes: MyTimesService,
		private comego: ComegoService,
		private comegoQuery: ComegoQuery,
		private recordService: RecordToolbarService,
		private app: AppService,
		@Inject(MAT_DIALOG_DATA) private data: ComeGoUpdateDialogData
	) {}
	readonly isLoading = createRxValue(false);
	get isPastTime() {
		return this.data.isPastTime;
	}
	get handleRuleErrors() {
		return !!this.data?.handleRuleErrors;
	}
	readonly maxLength = COMEGO_MAX_DESCRIPTION;
	readonly group = new FormGroup(
		{
			name: new FormControl<string>(null, [Validators.maxLength(this.maxLength)]),
			user: new FormControl<Workspace["users"][0]>(null),
			start: new FormControl(null),
			end: new FormControl(null),
			date: new FormControl(new Date(), {
				validators: [
					VerifyBlockComegoPast(
						() => this.userSettingsQuery.getValue(),
						() => {
							const user = this.group?.getRawValue()?.user as WorkspaceUser;
							if (!user) return null;
							const id = user.id;
							return id;
						},
						() => {
							const start: string = this.group.getRawValue()?.start;
							if (!start) return null;
							return start;
						},
						() => {
							const end: string = this.group.getRawValue()?.end;
							if (!end) return null;
							return end;
						}
					),
				],
			}),
			type: new FormControl<ComegoTimeType>("work"),
		},
		[
			CustomValidators.timeDiffCheck(
				"start",
				"end",
				false,
				(() => {
					const original = this.data.entity;

					return (key) => {
						if (!original) return null;
						if (key === "start") return startOfDay(new Date(original.start));
						return startOfDay((original.end && new Date(original.end)) || new Date()); // running time - set to now
					};
				})()
			),
			// (ctrl) => {
			//   if (this.rulebreaks$.value) return this.rulebreaks$.value as {};
			//   return null;
			// },
		]
	);
	readonly values$ = this.group.valueChanges.pipe(map(() => this.group.getRawValue()));

	readonly absenceSubcategories$value = createRxValue(
		this.userSettingsQuery.getValue().workspace?.["workinghours"]?.[
			"absenceSubcategories"
		] as absenceSubcategories[]
	);
	readonly isSubAbsence = combineLatest([
		this.absenceSubcategories$value.asObservable(),
		this.group.valueChanges.pipe(startWith(this.group.value)),
	]).pipe(
		map(([absences, formGroup]) => {
			return !!absences.find((absence) => absence.id == formGroup.type);
		})
	);

	readonly rulebreaks$ = fromRxValue<any | null>(
		combineLatest([this.values$, this.userSettingsQuery.select()]).pipe(
			map(([value, user]) => {
				return null;
			})
		)
	);
	readonly duration$ = combineLatest([this.values$, this.group.statusChanges]).pipe(
		startWith([this.group.value]),
		map(([x]) => {
			let acc = {
				type: x.type,
				typeName: this.calcType(x.type),
				sum: null as number,
			};
			const original = this.data.entity;
			if (!x.start) return null;
			if (!this.recording && !x.end) return null;
			const start = coerceTimeFormat(x.start!, startOfDay(x.date)),
				end = coerceTimeFormat(
					this.recording ? new Date() : x.end,
					startOfDay(!original.end ? new Date() : x.date)
				);
			if (end < start) return null;
			log.debug("duration: ", x.date, start, end, original);
			acc.sum = (end.getTime() - start.getTime()) / 1000;
			return acc;
		})
	);
	readonly recording$ = combineLatest([
		this.duration$,
		timer(0, 1000).pipe(
			untilDestroyed(this),
			takeWhile(() => this.recording)
		),
	]).pipe(
		combineLatestWith(this.values$),
		map(([, x]) => {
			if (!x) return null;
			let acc = {
				type: x.type,
				sum: null as number,
			};
			const original = this.data.entity;
			if (!x.start) return null;
			if (!this.recording && !x.end) return null;
			const start = coerceTimeFormat(x.start!, startOfDay(x.date)),
				end = coerceTimeFormat(
					this.recording ? new Date() : x.end,
					startOfDay(!original.end ? new Date() : x.date)
				);
			if (end < start) return null;
			log.debug("recording: ", x.date, start, end, original);
			acc.sum = (end.getTime() - start.getTime()) / 1000;
			return acc;
		})
	);
	readonly workingHoursError$ = combineLatest([
		this.values$,
		this.group.statusChanges,
		this.userSettingsQuery.select(),
	]).pipe(
		startWith([this.group.getRawValue(), null, this.userSettingsQuery.getValue()] as [
			typeof this.group.value,
			any,
			UserSettings,
		]),
		map(([, , _currentUser]) => {
			const value = this.group.getRawValue();
			if (!value?.date || !value.start || !value.end) return null;
			const user = { ..._currentUser, ...(value.user?.id && { id: value.user.id }) };
			if (!user?.workspace?.schedules?.workspace?.["workinghours"]) return null;

			const times = this.comegoQuery.getAll({
				filterBy: (x) =>
					isSameDay(new Date(x.start), value.date!) &&
					x.user?.id === user.id &&
					(this.data.entity?.id ? x.id !== this.data.entity.id : true),
			});
			const tstart = coerceTimeFormat(value.start, value.date!),
				tend = coerceTimeFormat(value.end, value.date!);
			const sum = times.reduce(
				(acc, r) => {
					const start = new Date(r.start),
						end = (r.end && new Date(r.end)) || new Date();
					acc[r.type] += end.getTime() - start.getTime();
					return acc;
				},
				{ absence: 0, pause: 0, work: 0 } as {
					[K in ComegoTimeType]: number;
				}
			);
			const valueDuration = tend.getTime() - tstart.getTime();
			sum[value.type] += clamp(valueDuration, 0, valueDuration);
			const wh: WorkingHourSettings = user.workspace.schedules.workspace["workinghours"];
			if (!wh) return null;
			const sumValue = sum.work;
			const pauseSum = sum.pause;
			const type = (!wh.preventDeviatingWorkingHours && "info") || null;
			const ignoredCTypes = ["absence"] as ComegoTimeType[];
			const cTypeIgnored = ignoredCTypes.includes(value.type);
			if (type === "info") return null;
			if (typeof wh.dailyMaxHours === "number" && sum.work > wh.dailyMaxHours * 3600 * 1000 && !cTypeIgnored) {
				return {
					content: "errors.times.comego.dailyMaxHours",
					args: resolveRawArgs({ dailyMaxHours: hoursToFormat(wh.dailyMaxHours) }),
					type,
				};
			}
			if (typeof wh.breakBetweenWorkingDays === "number" && wh.breakBetweenWorkingDays > 0.0 && !cTypeIgnored) {
				const pastDay = addDays(value.date.getTime(), -1);
				const nextDay = addDays(value.date.getTime(), 1);
				const lastWorkingDayTime = this.comegoQuery.getAll({
					filterBy: (x) =>
						isSameDay(new Date(x.start), pastDay) &&
						x.user?.id === user.id &&
						x.type === "work" &&
						(this.data.entity?.id ? x.id !== this.data.entity.id : true),
					limitTo: 1,
					sortBy(a, b) {
						const aend = new Date(a.end || new Date()),
							bend = new Date(b.end || new Date());
						return aend.getTime() - bend.getTime();
					},
					sortByOrder: Order.DESC,
				})?.[0];
				const nextWorkingDayTime = this.comegoQuery.getAll({
					filterBy: (x) =>
						isSameDay(new Date(x.start), nextDay) &&
						x.user?.id === user.id &&
						x.type === "work" &&
						(this.data.entity?.id ? x.id !== this.data.entity.id : true),
					limitTo: 1,
					sortBy(a, b) {
						const atime = new Date(a.start || new Date()),
							btime = new Date(b.start || new Date());
						return atime.getTime() - btime.getTime();
					},
					sortByOrder: Order.ASC,
				})?.[0];
				const minBreakBetweenDay = wh.breakBetweenWorkingDays * 3600 * 1000;
				if (
					(lastWorkingDayTime?.end &&
						tstart.getTime() - new Date(lastWorkingDayTime.end ?? tstart.getTime()).getTime() <
							minBreakBetweenDay) ||
					(nextWorkingDayTime?.start &&
						new Date(nextWorkingDayTime.start ?? tend.getTime()).getTime() - tend.getTime() <
							minBreakBetweenDay)
				) {
					return {
						content: "errors.times.comego.breakBetweenWorkingDays",
						args: resolveRawArgs({ breakBetweenWorkingDays: wh.breakBetweenWorkingDays }),
						type,
					};
				}
			}
			if (wh.breaks?.length && !cTypeIgnored) {
				const breakRule = wh.breaks
					.slice()
					.sort((b, a) => a.workingHours - b.workingHours)
					.find((x) => {
						const workingHourMs = x.workingHours * 3600 * 1000;
						const pauseHourMs = x.minutesDue * 3600 * 1000;
						return workingHourMs < sumValue && pauseHourMs > pauseSum;
					});
				if (breakRule)
					return {
						content: "errors.times.comego.breakRuleNotice",
						args: resolveRawArgs(breakRule),
						type,
					};
			}
			return null;
		}),
		tap((x) => log.debug("wh errors", x))
	);
	get groupValue() {
		return this.group.getRawValue();
	}
	readonly workspace$isAdmin = fromRxValue(
		this.userSettingsQuery.select((x) => hasPermission(DEFAULT_PERMISSION_GROUPS.Admin, x))
	);
	readonly workspace$canManageOthers = fromRxValue(
		this.userSettingsQuery.select((x) => isAdmin(x) || isSupervisor(x) || hasUserRoleManager(x))
	);
	readonly groupError$ = combineLatest([this.group.statusChanges, this.values$]).pipe(
		map(() => this.group.errors),
		tap((err) => log.debug("errors", err))
	);
	get recording() {
		return this.data.recording || !this.data.entity.end;
	}
	readonly comegoDescriptionToggle$ = createRxValueWithCache<boolean>("comgoDescriptionToggle", false);
	readonly isAMPM$ = this.userSettingsQuery.select().pipe(map((x) => !x.settings?.timeFormat24h));
	getErrorObservable(controlName: string) {
		const aliasMap = {
			name: "time.name",
			start: "timer.time.start",
			end: "timer.time.end",
		};
		const aliasName = aliasMap[controlName] || controlName;
		return useFormErrorObservable(this)(
			controlName,
			() => this.group.controls[controlName],
			{
				required: (error, ctrl) => {
					if (controlName === "task") return { content: "errors.record.desc-required", args: {} };
					if (controlName === "project") return { content: "errors.record.project-req", args: {} };
					return {
						content: "errors.required",
						args: { field: aliasName },
					};
				},
				minlength: (error, ctrl) => ({
					content: "errors.minlength",
					args: { field: aliasName, length: error.requiredLength },
				}),
				maxlength: (error, ctrl) => ({
					content: "errors.maxlength",
					args: { field: aliasName, length: error.requiredLength },
				}),
				same: (error, ctrl) => {
					const aliasFields = Object.entries(ctrl.errors?.fields || {}).reduce(
						(acc, [key, value]) => ({ ...acc, [value as string]: aliasMap[value as string] }),
						{}
					);
					return { content: "errors.sameValue", args: { ...aliasFields } };
				},
				range: (error, ctrl) => {
					return { content: "errors.times.comego.intervening", args: {} };
				},
				sameType: (error, ctrl) => {
					return { content: "Subsequent entries must not be the same type", args: {} };
				},
				missingType: (error, ctrl) => {
					return { content: "Entries are missing a stop entry", args: {} };
				},
				invalidType: (error, ctrl) => {
					return { content: 'Before last type must not be of type "pause"', args: {} };
				},
				intervening: (error, ctrl) => {
					return { content: "errors.times.comego.intervening", args: {} };
				},
				blockEdit: (error, ctrl) => {
					return { content: "errors.times.comego.blockEdit", args: {} };
				},
				blockEditFuture: (error, ctrl) => {
					return { content: "errors.times.comego.blockEditFuture", args: {} };
				},
				blockComegoPast: () => {
					const usersettings = this.userSettingsQuery.getValue();
					const blockComego = usersettings.workspace.settings.blockComego;
					return {
						content: "errors.blockComegoPast",
						args: { value: blockComego.period, range: blockComego.range },
					};
				},
				blockComegoFuture: () => {
					const usersettings = this.userSettingsQuery.getValue();
					const blockComego = usersettings.workspace.settings.blockComego;
					return {
						content: "errors.blockComegoFuture",
						args: { value: blockComego.futurePeriod, range: blockComego.futureRange },
					};
				},
			},
			(key) => key,
			{
				initialValidate: true,
			}
		);
	}
	readonly postDiffUser$ = combineLatest([
		this.values$.pipe(
			map((x) => x?.user?.id),
			distinctUntilChanged()
		),
		this.userSettingsQuery.select(),
	]).pipe(map(([uid, user]) => uid && user && uid !== user.id));
	private checkValueOnceChange(value: any) {
		this.values$
			.pipe(
				untilDestroyed(this),
				filter((x) => !!x),
				map(({ start, end }) => stringify({ start, end }) === stringify(value)),
				tap((x) => log.debug("break rule check", x)),
				takeWhile((x) => x, true)
			)
			.subscribe((x) => !x && this.error$.next(null));
	}
	readonly canUpdateTimes$ = this.userSettingsQuery
		.select()
		.pipe(map((x) => hasPermissionByKey(x, "groupsCanComegoUpdateTimes" as any)));
	readonly canDelete$ = combineLatest([this.canUpdateTimes$]).pipe(
		map(([canUpdate]) => {
			return canUpdate || this.recording;
		})
	);
	async ngOnInit() {
		this.ref.updateSize("560px");
		this.ref.addPanelClass(["mat-dialog-vanilla", "mat-dialog-relative"]);
		const hostDisableClose = this.ref.disableClose;
		this.group.valueChanges.pipe(startWith(this.group.getRawValue()), untilDestroyed(this)).subscribe(() => {
			this.ref.disableClose = hostDisableClose || (!this.recording && this.group.dirty);
		});
		this.ref
			.keydownEvents()
			.pipe(untilDestroyed(this))
			.subscribe((k) => k.key === "Escape" && this.dismiss());
		this.ref
			.beforeClosed()
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.error$.next(null);
			});
		if (this.data.fetchFromServer) {
			this.isLoading.next(true);
			await this.comego.adapter
				.post<[ComegoTime]>("/get/comego", {
					$filter: `id eq '${this.data.entity.id}'`,
				})
				.toPromise()
				.then(([time]) => {
					this.data.entity = time;
				})
				.catch((err) => {
					pushError(err);
					this.ref.close();
				})
				.finally(() => {
					this.isLoading.next(false);
				});
		}
		const user = this.userSettingsQuery.getValue();
		const time = this.data.entity,
			wstart = new Date(time.start),
			start = format(wstart, "HH:mm"),
			end = format(new Date(time.end || Date.now()), "HH:mm");
		this.group.setValue(
			{
				name: time.name ?? null,
				user: user.workspace.users.find((x) => x.id === this.data.entity.user.id),
				start,
				end,
				date: startOfDay(wstart),
				type: time["typeId"] ? time["typeId"] : time.type,
			},
			{ emitEvent: true }
		);
		if (time.end && !this.data.enableSubmit)
			this.group.addValidators(
				CustomValidators.controlMustChangeWithMap(this.group.value, {
					user: (x) => x.id,
				})
			);
		if (this.recording) {
			const canUpdateTimes = hasPermissionByKey(user, "groupsCanComegoUpdateTimes" as any);
			if (!canUpdateTimes) {
				this.group.controls.date.disable();
				this.group.controls.start.disable();
			}
		}
		this.group.updateValueAndValidity();
		this.group.markAllAsTouched();
		nextTick(() => {
			this.group.patchValue(this.group.getRawValue()); // trigger form group subscriptions
			if (this.data?.error) {
				this.error$.next(this.data.error);
				this.checkValueOnceChange(this.group.value);
			}
		});
		this.group.controls.type.valueChanges.subscribe(() => {
			if (
				this.group.controls.type.value !== "work" &&
				this.group.controls.type.value !== "pause" &&
				this.group.controls.type.value !== "absence"
			)
				this.parseTimeDurationFromType();
		});

		this.group.controls.start.valueChanges.subscribe(() => {
			this.group.controls.date.updateValueAndValidity();
			this.parseTimeDurationFromType();
		});

		this.absenceSubcategories = user?.workspace?.["workinghours"]?.["absenceSubcategories"] || [];
		this.group.controls.end.valueChanges.subscribe(() => {
			this.group.controls.date.updateValueAndValidity();
		});
		if (this.data.enableSubmit) {
			this.group.markAsDirty();
			this.group.updateValueAndValidity();
		}
	}
	readonly currentDateHasSchedule$ = combineLatest([this.values$, this.userSettingsQuery.select()]).pipe(
		map(([value, user]) => {
			if (!value?.date) return null;
			const sched = getActiveSchedule({ ...user, id: value.user?.id || user.id }, value.date);
			return (
				sched?.enabled && !!sched.items.find((x) => x.day === startOfDay(value.date).getUTCDay() && x.enabled)
			);
		})
	);
	openUserPicker() {
		if (this.recording) {
			return;
		}

		const user = this.group.value?.user;
		if (!user) return;
		this.dialog
			.open(UserSinglePickerDialogComponent, {
				data: <UserSinglePickerDialogData>{
					selected: this.group.value?.user,
					filter: ({ selected, ...x }) => {
						const status = ((user) => parseSubscriptionAsStatus(user.workspace, user))(
							this.userSettingsQuery.getValue()
						);
						return ((x.has_license || status.isTrial) && !x.removed) || selected;
					},
				},
			})
			.afterClosed()
			.subscribe((user) => {
				if (user) {
					this.patchValue({
						user,
					});
					this.group.controls.date.updateValueAndValidity();
				}
			});
	}
	patchValue(data: Partial<typeof this.group.value> = {}, options?: Parameters<typeof this.group.patchValue>[1]) {
		const user = this.userSettingsQuery.getValue();
		if (data.user === null) data.user = user.workspace.users.find((x) => x.id === user.id);
		return this.group.patchValue(data, options);
	}

	/** Close without saving or checking out; recording keeps running. */
	dismiss(): void {
		this.error$.next(null);
		this.ref.close();
	}
	async submit() {
		let { start, end, user, type, date, name } = this.group.getRawValue();
		let typeId: string = null;
		const validTypes: ComegoTimeType[] = ["work", "pause", "absence"];
		if (!validTypes.includes(type)) {
			typeId = type;
			type = "absence";
		}
		this.isLoading.update(true);
		const time = produce(this.data.entity, (draft) => {
			draft.name = name;
			draft.start = coerceTimeFormat(start, date).toISOString();
			if (this.recording)
				draft.end = roundToNearestMinutes(new Date(), { roundingMethod: "ceil", nearestTo: 1 }).toISOString();
			else draft.end = coerceTimeFormat(end, date).toISOString();
			draft.type = type;
			if (user) draft.user = user;
			draft["typeId"] = typeId;
		});
		if (this.recording) {
			const openRecording = {
				...this.data.entity,
				end: null,
				user: user ?? this.data.entity?.user,
			} as ComegoTime;
			if (await this.recordService.hasStopRuleViolation(openRecording)) {
				this.isLoading.update(false);
				this.ref.close(null);
				await this.recordService.resolveComegoStopAfterClosingParent(openRecording);
				return;
			}
			const { entry } = await this.recordService.stopComegoWithRuleCheck(openRecording, () =>
				this.checkoutRecording(time)
			);
			this.isLoading.update(false);
			if (!entry) return;
			this.ref.close(entry);
			return;
		}
		await this.comego
			.update(time)
			.then(this.recordService.handleWorkingHourSuccess.bind(this.recordService))
			.catch(async (err: HttpErrorResponse) => {
				this.isLoading.update(false);
				if (isWorkingHoursError(err.error)) {
					this.error$.next(parseErrorResponse(err));
					this.checkValueOnceChange({ start, end });
				} else {
					pushError(err);
				}
				return await Promise.reject(err);
			})
			.then(flattenEntityResponse)
			.then((x) => {
				if (x) this.comegoQuery.__store__.upsertMany(x);
				this.ref.close(x);
			});
		this.isLoading.update(false);
	}
	private checkoutRecording(time: ComegoTime): Promise<ComegoTime[] | null> {
		return this.comego
			.update(time)
			.then(this.recordService.handleWorkingHourSuccess.bind(this.recordService))
			.catch(async (err: HttpErrorResponse) => {
				if (isWorkingHoursError(err.error)) {
					this.error$.next(parseErrorResponse(err));
					this.checkValueOnceChange(this.group.getRawValue());
				} else {
					pushError(err);
				}
				return await Promise.reject(err);
			})
			.then(flattenEntityResponse)
			.then((x) => {
				if (x) this.comegoQuery.__store__.upsertMany(x);
				return x;
			});
	}

	allowedDates() {
		const pastDate = getComegoLockedDate(this.userSettingsQuery.getValue(), this.group.value.user?.id);
		const futureDate = getComegoFutureLockedDate(this.userSettingsQuery.getValue(), this.group.value.user?.id);
		return [pastDate, futureDate];
	}

	openCalPicker() {
		if (this.recording) {
			return;
		}

		const dates = this.allowedDates();
		return this.dialog
			.open(TimeDatePickerComponent, {
				data: <TimeDatePickerConfig>{
					selectedDate: new Date(this.group.value.date),
					minDate: dates[0],
					maxDate: dates[1],
				},
			})
			.afterClosed()
			.pipe(filter((x) => x && isValid(x)))
			.subscribe((x) => {
				this.patchValue({
					date: x,
				});
			});
	}
	async delete(confirmDeleteTemplate?: TemplateRef<any>) {
		this.isLoading.next(true);
		this.group.disable();
		if (confirmDeleteTemplate) {
			const allowDelete = await this.dialog
				.open(confirmDeleteTemplate, {
					data: {
						entity: this.data,
					},
				})
				.afterClosed()
				.toPromise()
				.then((x) => x === true)
				.catch(() => false);
			if (!allowDelete) {
				this.isLoading.next(false);
				this.group.enable();
				return;
			}
		}
		const entityResult = { ...this.data.entity, deleted: true };
		await this.comego.delete(this.data.entity as any);
		if (this.recording) {
			this.recordService.notifySuccess("success.recordingDiscarded");
		}
		this.ref.close([entityResult]);
	}
	selectInput(ev: Event, timeInput: ElRefDirective) {
		const el: HTMLElement = timeInput.elementRef.nativeElement;
		if (!el) {
			return;
		}
		const input = el.querySelector("input");
		if (input) {
			ev.preventDefault();
			input.select();
		}
	}
	calcType(type: string) {
		switch (type) {
			case "work":
			case "pause":
			case "absence":
				return type;
			default:
				return "absence";
		}
	}
	calcIcon(type: string) {
		switch (type) {
			case "work_history":
			case "pause":
			case "home_filled":
				return type;
			default:
				return "beach_access";
		}
	}

	parseTimeDurationFromType() {
		const userSettings = this.userSettingsQuery.getValue();
		const absenceType = this.absenceSubcategories.find((absence) => absence.id === this.group.controls.type.value);
		const userId = this.group.controls.user.value.id;
		this.group.controls.end.enable();
		if (!absenceType) return;
		if (absenceType.isStatic || absenceType.durationType == "basedOnTargetHours") {
			if (!isAdmin(userSettings) && !isSubordinate(userSettings, userId)) this.group.controls.end.disable();
		}
		const date = this.group.controls.date.value;
		let duration = "";
		if (absenceType.durationType == "basedOnTargetHours") {
			this.group.controls.end.enable();
			duration = this.getScheduleForUser(userId, date);
		} else {
			duration = absenceType.duration;
		}
		const endString = this.sumTimes([this.group.controls.start.value, duration]);
		this.group.controls.end.patchValue(endString);
	}

	getScheduleForUser(userId: string, date: Date) {
		const user = this.userSettingsQuery.getValue();
		const times = generateTimeTableFromSchedule({ ...user, id: userId }, date);

		if (!times || times.length == 0) return "00:00";

		let hours: string[] = [];
		times
			.filter((time) => {
				return time.type == "work";
			})
			.forEach((time) => {
				hours.push(this.calculateScheduleDuration(time));
			});

		return this.sumTimes(hours);
	}
	sumTimes(times: string[]): string {
		let totalMinutes = 0;

		times.forEach((time) => {
			const [hours, minutes] = time.split(":").map(Number);
			totalMinutes += hours * 60 + minutes;
		});

		const sumHours = Math.floor(totalMinutes / 60);
		const sumMinutes = totalMinutes % 60;

		return `${String(sumHours).padStart(2, "0")}:${String(sumMinutes).padStart(2, "0")}`;
	}
	calculateScheduleDuration(entry: ComeGoTime): string {
		const [startHour, startMinute] = entry.start.split(":").map(Number);
		const [endHour, endMinute] = entry.end.split(":").map(Number);

		const startTotal = startHour * 60 + startMinute;
		const endTotal = endHour * 60 + endMinute;

		const diffMinutes = endTotal - startTotal;

		const hours = Math.floor(diffMinutes / 60);
		const minutes = diffMinutes % 60;

		return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
	}
	typeIsSubAbsence(type: string) {
		if (type == "work" || type === "pause") return false;
		return true;
	}

	readonly absencesForUser = useAbsencesForUser();
}
